/** Variant subclass barrel. */

import { installRowFaces } from "../model-row-reads.ts";
import { kindOfLiveRow } from "../row-kind.ts";

// Each row's variant reads face (`rowReadsOf`), installed where every
// variant is loaded.
installRowFaces((row) => kindOfLiveRow(row).row);

// Role bases
export { SourceRow } from "./source-row.ts";
export { LayoutRow } from "./layout-row.ts";
export { RendererRow } from "./renderer-row.ts";
export type { RendererAttrs } from "./renderer-row.ts";

// Alias
export { AliasRow } from "./alias-row.ts";
export { CalendarAliasRow } from "./calendar-alias-row.ts";

// Source variants — there are exactly two: `DataSource` (every user-
// or provider-backed row across every (dtype, cardinality) combo)
// and `FormulaDataSource` (cells owned by the formula engine). Typed
// cell access for scalar dtypes lives on the auto-hydrated
// `column.*` renderer child (renderer-as-editor); typed access for
// `obj` dtypes goes through facades (`row.as(FACADE_KEY)`).
export { DataSource } from "./data-source.ts";
export { FormulaDataSource, formulaWriterOf } from "./formula-data-source.ts";
export { ComputedDataSource } from "./computed-data-source.ts";
export {
  calendarSystemCellData,
  CalendarSystemRow,
  normalizeCalendarSystemName,
} from "./calendar-system-row.ts";
export { ListDataSource } from "./list-data-source.ts";
export type { ComputedSourceBuilder } from "./computed-source-registry.ts";

// Layout variants
export { BlockRow } from "./block-row.ts";
export { CalendarBodyRow } from "./calendar-body-row.ts";
export { TimedGridConfig } from "./timed-grid-config.ts";
export {
  type SwitchCase,
  type SwitchConfig,
  selectLayout,
  type SwitchWhen,
} from "./switch-config.ts";
export { CanvasRow } from "./canvas-row.ts";
export { ColumnRow } from "./column-row.ts";
export { GridRow } from "./grid-row.ts";
export { GroupRow } from "./group-row.ts";
export { HeaderRow } from "./header-row.ts";
export { InertRow } from "./inert-row.ts";
export { TimelineColumnRow } from "./timeline-column-row.ts";
export { TimelineRowRow } from "./timeline-row-row.ts";

// Renderer variants — column slot
export { ColumnCheckmarkRow, columnCheckmarkWriterOf } from "./column-checkmark.ts";
export { ColumnGraphRow, columnGraphWriterOf } from "./column-graph.ts";
export { ColumnHeatmapRow } from "./column-heatmap.ts";
export { ColumnNumberRow, columnNumberWriterOf } from "./column-number.ts";
export { ColumnSeriesSummaryRow } from "./column-series-summary.ts";
export { ColumnTextRow, columnTextWriterOf } from "./column-text.ts";
export { ColumnLogsRow } from "./column-logs.ts";
export { ColumnTimelineRow } from "./column-timeline.ts";
export { ColumnTimezoneRow } from "./column-timezone.ts";
export { ColumnWeatherRow } from "./column-weather.ts";

// Renderer variants — canvas slot
export { CanvasBarRow } from "./canvas-bar-row.ts";
export { CanvasHRuleRow } from "./canvas-hrule-row.ts";
export { CanvasLineRow } from "./canvas-line-row.ts";
export { CanvasPointsRow } from "./canvas-points-row.ts";
export { CanvasRegressionBandRow } from "./canvas-regression-band-row.ts";
export { CanvasRegressionCurveRow } from "./canvas-regression-curve-row.ts";
export { CanvasRegressionFillRow } from "./canvas-regression-fill-row.ts";

// Renderer variants — timeline[row] + timeline[column] slots
export { TimelineEventRow } from "./timeline-event-row.ts";
export { TimelineNumberRow } from "./timeline-number-row.ts";
export { TimelineSpanRow } from "./timeline-span-row.ts";
export { TimelineTextRow } from "./timeline-text-row.ts";

// Renderer variants — grid slot
export { GridEventRow } from "./grid-event-row.ts";
export { GridTextRow } from "./grid-text-row.ts";

// Default-renderer lookup (per-slot dispatch for a dataSource's auto-
// hydrated renderer child). See renderer-lookup.ts for scope and
// the table.
export { resolveDefaultRendererId } from "./renderer-lookup.ts";
export {
  hydrateDefaultRenderer,
  instantiateRenderer,
  isSyntheticDefaultRendererId,
} from "./hydrate-default-renderer.ts";

// Registry
export { parseRow } from "./registry.ts";
